/**
 * Automatisches Hinzufuegen/Entfernen von Free Gifts im Theme (Cart-Drawer/Cart-Seite).
 *
 * Zwei unabhaengige Regelquellen werden hier zusammengefuehrt:
 * 1. Shop-Metafield `custom.free_gifts` (App "hensslers-discounts") - spiegelt die Logik
 *    der Checkout-UI-Extension. Siehe: hensslers-discounts/docs/theme-free-gift-integration.md
 *    und theme-active-flag.md (Regeln mit active === false werden ignoriert).
 * 2. Legacy-Settings aus `snippets/cart-gamification.liquid` (Free Gift zu jeder Bestellung,
 *    Conditional Gift per Page-Visit-Flag) - unabhaengig von custom.free_gifts, bewusst
 *    beibehalten, da die neue Config dafuer keine Entsprechung hat.
 *
 * Opt-out: Cart-Attribut `_gift_declined` (kommaseparierte numerische Variant-IDs),
 * gemeinsamer Contract mit der Checkout-Extension.
 * Siehe: hensslers-discounts/docs/theme-gift-declined.md
 */
(function () {
  const CONFIG_ELEMENT_ID = 'FreeGiftConfig';
  const FETCH_TIMEOUT_MS = 8000;
  const DECLINED_ATTR = '_gift_declined';

  let isSyncing = false;
  let rerunQueued = false;
  /** @type {Record<string, boolean>} zuletzt beobachtete Gift-Lines (Session) */
  const prevPresent = {};

  function fetchWithTimeout(url, options) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    return fetch(url, { ...options, signal: controller.signal }).finally(() => clearTimeout(timeout));
  }

  function readConfig() {
    const el = document.getElementById(CONFIG_ELEMENT_ID);
    if (!el) return null;
    try {
      const config = JSON.parse(el.textContent);
      if (!config) return null;
      return { config, country: el.dataset.country || '' };
    } catch (error) {
      console.error('[FreeGift] Konnte Config nicht parsen', error);
      return null;
    }
  }

  function readLegacyConfig() {
    const el = document.querySelector('.gamification');
    if (!el) return null;
    return {
      everyOrderVariantId: el.getAttribute('free_gift_every_order') || null,
      conditionalVariantId: el.getAttribute('conditional-gift-id') || null,
      conditionalThreshold: Number(el.dataset.conditionalThreshold) || null,
    };
  }

  function selectRules(config, country) {
    if (config.enabled === false) return [];
    const byCountry = config.byCountry || {};
    const countryRules = byCountry[country];
    const rawRules =
      Array.isArray(countryRules) && countryRules.length
        ? countryRules
        : Array.isArray(config.default)
          ? config.default
          : [];
    // active fehlt/true = aktiv; active === false = pausiert (theme-active-flag.md)
    return rawRules.filter((rule) => rule.active !== false);
  }

  function numericIdFromGid(value) {
    if (value == null) return null;
    const match = String(value).match(/(\d+)\s*$/);
    return match ? match[1] : null;
  }

  function parseDeclined(cart) {
    const raw = cart.attributes && cart.attributes[DECLINED_ATTR];
    if (!raw) return new Set();
    return new Set(
      String(raw)
        .split(',')
        .map((s) => s.trim())
        .filter((s) => /^\d+$/.test(s))
    );
  }

  function writeDeclined(ids) {
    const value = [...ids].join(',');
    return fetchWithTimeout(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        attributes: { [DECLINED_ATTR]: value },
      }),
    });
  }

  function computeAmount(cart) {
    const giftCardTotal = cart.items
      .filter((item) => item.gift_card)
      .reduce((sum, item) => sum + item.line_price, 0);
    return cart.total_price - giftCardTotal;
  }

  function findGiftLine(cart, variantIdNumeric) {
    return cart.items.find(
      (item) => String(item.variant_id) === variantIdNumeric && item.properties && item.properties._gift === 'true'
    );
  }

  function isScheduled(rule, now) {
    if (rule.startsAt && now < new Date(rule.startsAt)) return false;
    if (rule.endsAt && now >= new Date(rule.endsAt)) return false;
    return true;
  }

  /**
   * Pro Regel: Opt-out erkennen/resetten und Add/Change-Aktionen bauen.
   * @returns {{ additions: object[], changes: object[], declinedDirty: boolean }}
   */
  function processRule(cart, variantIdNumeric, qualifies, declined, properties) {
    const additions = [];
    const changes = [];
    let declinedDirty = false;
    /** Nach erfolgreichem Theme-Remove prevPresent auf false setzen (kein False-Opt-out) */
    let willSelfRemove = false;

    if (!variantIdNumeric) return { additions, changes, declinedDirty, willSelfRemove };

    const giftLine = findGiftLine(cart, variantIdNumeric);

    // 1) User-Remove: Gift war da, ist weg, Threshold/Schedule noch erfuellt
    if (prevPresent[variantIdNumeric] && !giftLine && qualifies) {
      if (!declined.has(variantIdNumeric)) {
        declined.add(variantIdNumeric);
        declinedDirty = true;
      }
    }

    // 2) Reset: nicht mehr qualifiziert → Opt-out loeschen
    if (!qualifies && declined.has(variantIdNumeric)) {
      declined.delete(variantIdNumeric);
      declinedDirty = true;
    }

    const wantsGift = qualifies && !declined.has(variantIdNumeric);

    if (wantsGift && !giftLine) {
      additions.push({
        id: parseInt(variantIdNumeric, 10),
        quantity: 1,
        properties: properties || { _gift: 'true' },
      });
    } else if (!qualifies && giftLine) {
      // Nur bei !qualifies entfernen — nicht wenn declined (User-Opt-out, Line schon weg)
      changes.push({ id: giftLine.key, quantity: 0 });
      willSelfRemove = true;
    } else if (wantsGift && giftLine && giftLine.quantity > 1) {
      changes.push({ id: giftLine.key, quantity: 1 });
    }

    // Beobachteter Stand VOR unseren Mutationen (Spec: prevPresent)
    prevPresent[variantIdNumeric] = Boolean(giftLine);

    return { additions, changes, declinedDirty, willSelfRemove, variantIdNumeric };
  }

  function buildActions(cart, rules, declined) {
    const additions = [];
    const changes = [];
    const selfRemoveIds = [];
    let declinedDirty = false;
    const amount = computeAmount(cart);
    const now = new Date();

    rules.forEach((rule) => {
      const variantIdNumeric = numericIdFromGid(rule.variantId);
      if (!variantIdNumeric || !rule.threshold) return;

      const qualifies = amount >= rule.threshold * 100 && isScheduled(rule, now);
      const result = processRule(cart, variantIdNumeric, qualifies, declined, {
        _gift: 'true',
        _gift_title: rule.title || '',
      });
      additions.push(...result.additions);
      changes.push(...result.changes);
      if (result.declinedDirty) declinedDirty = true;
      if (result.willSelfRemove) selfRemoveIds.push(variantIdNumeric);
    });

    return { additions, changes, declinedDirty, selfRemoveIds };
  }

  function collectAllConfiguredVariantIds(config) {
    const ids = new Set();
    const collect = (rules) =>
      (rules || []).forEach((rule) => {
        const id = numericIdFromGid(rule.variantId);
        if (id) ids.add(id);
      });
    collect(config.default);
    Object.values(config.byCountry || {}).forEach(collect);
    return ids;
  }

  function collectManagedOrphanGiftLines(cart, variantIds) {
    return cart.items
      .filter((item) => item.properties && item.properties._gift === 'true' && variantIds.has(String(item.variant_id)))
      .map((item) => ({ id: item.key, quantity: 0 }));
  }

  function buildLegacyActions(cart, legacyConfig, declined) {
    const additions = [];
    const changes = [];
    const selfRemoveIds = [];
    let declinedDirty = false;
    if (!legacyConfig) return { additions, changes, declinedDirty, selfRemoveIds };

    const amount = computeAmount(cart);
    const { everyOrderVariantId, conditionalVariantId, conditionalThreshold } = legacyConfig;

    if (everyOrderVariantId) {
      const qualifies = amount > 0;
      const result = processRule(cart, everyOrderVariantId, qualifies, declined, { _gift: 'true' });
      additions.push(...result.additions);
      changes.push(...result.changes);
      if (result.declinedDirty) declinedDirty = true;
      if (result.willSelfRemove) selfRemoveIds.push(everyOrderVariantId);
    }

    if (conditionalVariantId && conditionalThreshold) {
      const hasVisitedFlag = window.PageVisitTracker?.hasVisited() || false;
      const qualifies = hasVisitedFlag && amount >= conditionalThreshold;
      const result = processRule(cart, conditionalVariantId, qualifies, declined, {
        _gift: 'true',
        _conditional: 'true',
      });
      additions.push(...result.additions);
      changes.push(...result.changes);
      if (result.declinedDirty) declinedDirty = true;
      if (result.willSelfRemove) selfRemoveIds.push(conditionalVariantId);
    }

    return { additions, changes, declinedDirty, selfRemoveIds };
  }

  function addGifts(items) {
    return fetchWithTimeout(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ items }),
    });
  }

  function applyChange(change) {
    return fetchWithTimeout(window.Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(change),
    });
  }

  function getSectionInnerHTML(html, selector) {
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return el ? el.innerHTML : '';
  }

  function replaceSection(rootId, selector, html) {
    if (!html) return;
    const root = document.getElementById(rootId);
    if (!root) return;
    const target = root.querySelector(selector) || root;
    target.innerHTML = getSectionInnerHTML(html, selector);
  }

  function reinitCartWidgets() {
    requestAnimationFrame(() => {
      if (typeof window.initializeOrderCount === 'function') window.initializeOrderCount();
      if (typeof window.toggleGamificationVariant === 'function') window.toggleGamificationVariant();
    });
  }

  function refreshCartUI() {
    return fetchWithTimeout(
      window.Shopify.routes.root + '?sections=cart-drawer,cart-icon-bubble,main-cart-items,main-cart-footer'
    )
      .then((response) => response.text())
      .then((responseText) => {
        const parsedState = JSON.parse(responseText);
        replaceSection('CartDrawer', '.drawer__inner', parsedState['cart-drawer']);
        replaceSection('cart-icon-bubble', '.shopify-section', parsedState['cart-icon-bubble']);
        if (document.getElementById('main-cart-items')) {
          const container = document.getElementById('MainContent')?.querySelector('.shopify-section');
          if (container && parsedState['main-cart-items']) {
            container.innerHTML = getSectionInnerHTML(parsedState['main-cart-items'], '.shopify-section');
          }
        }
        reinitCartWidgets();
        document.dispatchEvent(new CustomEvent('free-gift:synced'));
      })
      .catch((error) => console.error('[FreeGift] UI-Refresh fehlgeschlagen', error));
  }

  async function runSync() {
    const configData = readConfig();
    const legacyConfig = readLegacyConfig();
    if (!configData && !legacyConfig) return;

    let cart;
    try {
      cart = await fetchWithTimeout(window.Shopify.routes.root + 'cart.js').then((r) => r.json());
    } catch (error) {
      console.error('[FreeGift] Cart konnte nicht geladen werden', error);
      return;
    }

    const declined = parseDeclined(cart);
    let additions = [];
    let changes = [];
    let selfRemoveIds = [];
    let declinedDirty = false;

    if (configData) {
      const rules = selectRules(configData.config, configData.country);
      const built = buildActions(cart, rules, declined);
      additions = additions.concat(built.additions);
      changes = changes.concat(built.changes);
      selfRemoveIds = selfRemoveIds.concat(built.selfRemoveIds);
      if (built.declinedDirty) declinedDirty = true;

      if (configData.config.enabled === false) {
        const managedVariantIds = collectAllConfiguredVariantIds(configData.config);
        changes = changes.concat(collectManagedOrphanGiftLines(cart, managedVariantIds));
      }
    }

    const legacyBuilt = buildLegacyActions(cart, legacyConfig, declined);
    additions = additions.concat(legacyBuilt.additions);
    changes = changes.concat(legacyBuilt.changes);
    selfRemoveIds = selfRemoveIds.concat(legacyBuilt.selfRemoveIds);
    if (legacyBuilt.declinedDirty) declinedDirty = true;

    // Add-Guard: Declined nie hinzufuegen (Defense in Depth)
    additions = additions.filter((item) => !declined.has(String(item.id)));

    if (!additions.length && !changes.length && !declinedDirty) return;

    try {
      if (declinedDirty) await writeDeclined(declined);
      if (additions.length) {
        await addGifts(additions);
        additions.forEach((item) => {
          prevPresent[String(item.id)] = true;
        });
      }
      for (const change of changes) {
        await applyChange(change);
      }
      // Theme-Removes nicht als User-Opt-out werten (kein zweiter Sync noetig)
      selfRemoveIds.forEach((id) => {
        prevPresent[String(id)] = false;
      });
      if (additions.length || changes.length || declinedDirty) {
        await refreshCartUI();
      }
    } catch (error) {
      console.error('[FreeGift] Sync fehlgeschlagen', error);
    }
  }

  async function sync() {
    if (isSyncing) {
      rerunQueued = true;
      return;
    }
    isSyncing = true;
    try {
      await runSync();
    } finally {
      isSyncing = false;
      if (rerunQueued) {
        rerunQueued = false;
        sync();
      }
    }
  }

  // Einmalige Migration: altes localStorage-Flag entfernen
  try {
    localStorage.removeItem('blockgift');
  } catch (e) {
    /* ignore */
  }

  window.HensslersFreeGift = { sync };

  document.addEventListener('DOMContentLoaded', sync);
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, sync);
  }
})();
